import React from 'react';
import { Spinner } from '../UI/Spinner';

interface AccountSummaryCardProps {
  accountId: string;
  buyingPower: string;
  cash: string;
  isLoading?: boolean;
}

/**
 * Formats a string currency value for display
 * @param value - The string value representing currency
 * @returns Formatted currency string
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Account Summary Card component
 * Displays account balances including buying power and cash
 */
export function AccountSummaryCard({
  accountId,
  buyingPower,
  cash,
  isLoading = false,
}: AccountSummaryCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Summary</h2>
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="account-summary-heading"
      className="bg-white shadow rounded-lg p-6"
    >
      <h2
        id="account-summary-heading"
        className="text-lg font-semibold text-gray-900 mb-4"
      >
        Account Summary
      </h2>

      <dl className="space-y-4">
        {/* Buying Power */}
        <div>
          <dt className="text-sm text-gray-500 uppercase tracking-wide font-medium">
            Buying Power
          </dt>
          <dd className="mt-1 flex items-center">
            <span
              className="text-2xl font-semibold text-gray-900"
              aria-label={`Buying power: ${formatCurrency(buyingPower)}`}
            >
              {formatCurrency(buyingPower)}
            </span>
            <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              <span className="mr-1">●</span>
              Available
            </span>
          </dd>
        </div>

        {/* Cash Balance */}
        <div>
          <dt className="text-sm text-gray-500 uppercase tracking-wide font-medium">
            Cash Balance
          </dt>
          <dd className="mt-1">
            <span
              className="text-2xl font-semibold text-gray-900"
              aria-label={`Cash balance: ${formatCurrency(cash)}`}
            >
              {formatCurrency(cash)}
            </span>
          </dd>
        </div>

        {/* Account ID */}
        <div className="pt-2 border-t border-gray-100">
          <dt className="text-sm text-gray-500">Account</dt>
          <dd className="mt-1">
            <span className="text-sm text-gray-600 font-mono">{accountId}</span>
          </dd>
        </div>
      </dl>
    </section>
  );
}
